import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Chip, Divider, Tooltip, Typography } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export function servicePartPhotoUrl(part) {
  return part?.thumbnail_url || part?.photos?.[0]?.url || part?.photo_url || '';
}

export function serviceInventoryPartSearchText(part) {
  return [
    part?.part_type,
    part?.name,
    part?.description,
    part?.category,
    part?.sku,
    part?.biomed_part_number_sku,
    part?.part_number_oem,
    part?.serial_number,
    part?.cross_reference,
    part?.make,
    part?.model,
    part?.location,
    part?.source_locations?.join?.(' '),
    part?.notes,
    part?.condition_notes,
  ].filter(Boolean).join(' ').toLowerCase();
}

export function ServicePartThumbnail({ part, size = 54 }) {
  const url = servicePartPhotoUrl(part);
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [url]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #d6e1ec',
        bgcolor: '#edf3f8',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {url && !failed ? (
        <Box
          component="img"
          src={url}
          alt={part?.part_type || part?.sku || 'Inventory part'}
          loading="lazy"
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Inventory2Icon sx={{ color: '#7890a8', fontSize: Math.round(size * 0.52) }} />
      )}
    </Box>
  );
}

function partValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function moneyValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toLocaleString(undefined, { style: 'currency', currency: 'CAD' }) : String(value);
}

function DetailItem({ label, value, wide = false }) {
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : 'auto' }}>
      <Typography sx={{ fontSize: '.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: .5, fontWeight: 900 }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: '#16324f', fontWeight: 750, whiteSpace: wide ? 'pre-wrap' : 'normal', wordBreak: 'break-word' }}>{partValue(value)}</Typography>
    </Box>
  );
}

function PartHoverDetails({ part, label }) {
  const sourceLocations = part.source_locations?.join?.(', ') || part.location;
  const serialNumbers = part.serial_numbers?.join?.(', ') || part.serial_number;
  const cameFrom = part.unit_came_off_from_name || part.came_off_vehicle_name || part.unit_came_off_from_location || part.unit_came_off_from;
  const intendedFor = part.unit_intended_for_name || part.intended_vehicle_name || part.unit_intended_for_location || part.unit_intended_for;
  const condition = [
    part.used_new === true ? 'New' : part.used_new === false ? 'Used' : null,
    part.condition_scale ? `${part.condition_scale}/5` : null,
  ].filter(Boolean).join(' • ');

  return (
    <Box sx={{ width: { xs: 290, sm: 420 }, maxWidth: 'calc(100vw - 32px)', p: .4 }}>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
        <ServicePartThumbnail part={part} size={78} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#102f4c', fontWeight: 950, fontSize: '1rem', lineHeight: 1.25 }}>{label}</Typography>
          <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mt: .8 }}>
            <Chip size="small" label={`Available: ${Number(part.quantity ?? 0).toLocaleString()}`} sx={{ bgcolor: '#e8f7ef', color: '#18734b', fontWeight: 900 }} />
            <Chip size="small" label={condition || 'Condition not recorded'} sx={{ bgcolor: '#edf4fb', color: '#315f7d', fontWeight: 800 }} />
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1.2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.05, maxHeight: 360, overflowY: 'auto', pr: .4 }}>
        <DetailItem label="Part Type" value={part.part_type || part.name} />
        <DetailItem label="Category" value={part.category} />
        <DetailItem label="BioMed SKU" value={part.biomed_part_number_sku || part.sku} />
        <DetailItem label="OEM Number" value={part.part_number_oem} />
        <DetailItem label="Serial Number" value={serialNumbers} />
        <DetailItem label="Cross Reference" value={part.cross_reference} />
        <DetailItem label="Make / Model" value={[part.make, part.model, part.year].filter(Boolean).join(' ')} />
        <DetailItem label="Inventory Location" value={sourceLocations} />
        <DetailItem label="Unit Came Off From" value={cameFrom} />
        <DetailItem label="Unit Intended For" value={intendedFor} />
        <DetailItem label="Storage Condition" value={part.condition_of_storage} />
        <DetailItem label="Recommended Storage" value={part.recommended_storage} />
        <DetailItem label="Purchase Price" value={moneyValue(part.purchase_price)} />
        <DetailItem label="Current Value" value={moneyValue(part.current_value)} />
        <DetailItem label="Description" value={part.description} wide />
        <DetailItem label="Part Notes" value={part.notes} wide />
        <DetailItem label="Condition Notes" value={part.condition_notes} wide />
      </Box>
    </Box>
  );
}

export function ServiceInventoryPartOption({ optionProps, part, selected, label }) {
  const { key, ...listItemProps } = optionProps;
  return (
    <Tooltip
      key={key}
      arrow
      enterDelay={280}
      enterNextDelay={120}
      leaveDelay={100}
      placement="right-start"
      title={<PartHoverDetails part={part} label={label} />}
      slotProps={{
        popper: { sx: { zIndex: 1600 } },
        tooltip: {
          sx: {
            maxWidth: 470,
            bgcolor: '#ffffff',
            color: '#16324f',
            border: '1px solid #c9d9e7',
            borderRadius: 2.5,
            boxShadow: '0 16px 42px rgba(16,42,67,.2)',
            p: 1.25,
          },
        },
        arrow: { sx: { color: '#ffffff', '&::before': { border: '1px solid #c9d9e7' } } },
      }}
    >
      <Box
        component="li"
        {...listItemProps}
        sx={{ ...listItemProps.sx, gap: 1.25, alignItems: 'center', minHeight: 76, py: 1 }}
      >
        <Checkbox checked={selected} sx={{ p: 0.5 }} />
        <ServicePartThumbnail part={part} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f' }}>{label}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            SKU: {part.sku || part.biomed_part_number_sku || '—'} • Available: {Number(part.quantity ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Location: {part.location || part.source_locations?.join?.(', ') || '—'} • OEM: {part.part_number_oem || '—'}
          </Typography>
        </Box>
        <InfoOutlinedIcon sx={{ color: '#6b8298', fontSize: 19, mr: .5 }} />
      </Box>
    </Tooltip>
  );
}
